import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  listPotd,
  listPublishedPhotographers,
  regenerateTodayPotd,
  setPotdForDate,
  deletePotd,
  type PotdRow,
  type PhotographerOption,
} from "@/lib/admin-api";

export const Route = createFileRoute("/admin/photo-of-the-day")({
  component: PotdAdminPage,
});

function todayUtc() {
  return new Date().toISOString().slice(0, 10);
}

function PotdAdminPage() {
  const [history, setHistory] = useState<PotdRow[]>([]);
  const [photographers, setPhotographers] = useState<PhotographerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editDate, setEditDate] = useState<string>(todayUtc());
  const [selectedPhotographerId, setSelectedPhotographerId] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<string>("");

  const today = todayUtc();
  const todayRow = history.find((h) => h.date === today) ?? null;

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [h, p] = await Promise.all([listPotd(), listPublishedPhotographers()]);
      setHistory(h);
      setPhotographers(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const selectedPhotographer = useMemo(
    () => photographers.find((p) => p.id === selectedPhotographerId) ?? null,
    [photographers, selectedPhotographerId],
  );

  async function onRegenerate() {
    setBusy("regen"); setError(null);
    try { await regenerateTodayPotd(); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : "Regenerieren fehlgeschlagen."); }
    finally { setBusy(null); }
  }

  async function onSave() {
    if (!selectedPhotographer || !selectedImage || !editDate) {
      setError("Datum, Fotograf und Bild auswählen.");
      return;
    }
    setBusy("save"); setError(null);
    try {
      await setPotdForDate(editDate, selectedPhotographer, selectedImage);
      setSelectedPhotographerId(""); setSelectedImage("");
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen."); }
    finally { setBusy(null); }
  }

  async function onDelete(date: string) {
    if (!confirm(`Eintrag für ${date} löschen?`)) return;
    setBusy(`del-${date}`); setError(null);
    try { await deletePotd(date); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : "Löschen fehlgeschlagen."); }
    finally { setBusy(null); }
  }

  return (
    <div>
      <div>
        <div className="eyebrow text-muted-foreground">Magazin</div>
        <h1 className="serif text-4xl md:text-5xl mt-3">Bild des Tages</h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-xl">
          Heute wird automatisch um 00:05 UTC ein Bild gewählt. Du kannst es manuell neu würfeln, gezielt setzen oder den Verlauf einsehen.
        </p>
      </div>

      {error && <div className="mt-6 border border-destructive text-destructive p-3 text-sm">{error}</div>}

      {/* Today */}
      <section className="mt-10 grid md:grid-cols-12 gap-8 border border-border p-5 md:p-6">
        <div className="md:col-span-5">
          <div className="eyebrow text-muted-foreground">Heute · {today}</div>
          {todayRow ? (
            <>
              <h2 className="serif text-2xl mt-3">{todayRow.photographer_name ?? ""}</h2>
              <div className="text-xs text-muted-foreground mt-1">{todayRow.region ?? ""}</div>
              <div className="text-xs text-muted-foreground mt-2 break-all">{todayRow.image_url}</div>
            </>
          ) : (
            <div className="serif italic text-muted-foreground mt-3">Noch nichts gewählt.</div>
          )}
          <div className="mt-6 flex flex-wrap gap-2">
            <button onClick={onRegenerate} disabled={!!busy}
              className="eyebrow bg-foreground text-background px-4 py-2 disabled:opacity-50">
              {busy === "regen" ? "Würfle …" : "Neu zufällig wählen"}
            </button>
            {todayRow && (
              <button onClick={() => onDelete(today)} disabled={!!busy}
                className="eyebrow border border-destructive text-destructive px-4 py-2 disabled:opacity-50">
                Heute löschen
              </button>
            )}
          </div>
        </div>
        <div className="md:col-span-7">
          {todayRow ? (
            <img src={todayRow.image_url} alt="Bild des Tages"
              className="w-full aspect-[4/3] object-cover bg-muted" />
          ) : (
            <div className="w-full aspect-[4/3] bg-muted" />
          )}
        </div>
      </section>

      {/* Manual set */}
      <section className="mt-10 border border-border p-5 md:p-6">
        <div className="eyebrow text-muted-foreground">Manuell setzen</div>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          <label className="block">
            <div className="eyebrow text-muted-foreground mb-2">Datum</div>
            <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)}
              className="w-full border border-border bg-transparent p-2 text-sm" />
          </label>
          <label className="block">
            <div className="eyebrow text-muted-foreground mb-2">Fotograf</div>
            <select value={selectedPhotographerId}
              onChange={(e) => { setSelectedPhotographerId(e.target.value); setSelectedImage(""); }}
              className="w-full border border-border bg-transparent p-2 text-sm">
              <option value="">wählen</option>
              {photographers.map((p) => (
                <option key={p.id} value={p.id}>{p.name} · {p.region ?? ""}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="eyebrow text-muted-foreground mb-2">Bild</div>
            <select value={selectedImage} onChange={(e) => setSelectedImage(e.target.value)}
              disabled={!selectedPhotographer}
              className="w-full border border-border bg-transparent p-2 text-sm disabled:opacity-50">
              <option value="">wählen</option>
              {selectedPhotographer?.gallery_urls.map((url, i) => (
                <option key={url} value={url}>Bild {i + 1}</option>
              ))}
            </select>
          </label>
        </div>
        {selectedImage && (
          <img src={selectedImage} alt="Vorschau"
            className="mt-4 w-full max-w-md aspect-[4/3] object-cover bg-muted" />
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={onSave} disabled={!!busy || !selectedPhotographer || !selectedImage}
            className="eyebrow bg-foreground text-background px-4 py-2 disabled:opacity-50">
            {busy === "save" ? "Speichere …" : "Für Datum speichern"}
          </button>
        </div>
      </section>

      {/* History */}
      <section className="mt-10">
        <div className="eyebrow text-muted-foreground mb-4">Verlauf</div>
        {loading && <div className="text-sm text-muted-foreground">Lädt …</div>}
        {!loading && history.length === 0 && <div className="text-sm text-muted-foreground">Keine Einträge.</div>}
        <div className="border border-border divide-y divide-border">
          {history.map((row) => (
            <div key={row.date} className="grid grid-cols-12 gap-3 items-center p-3 md:p-4">
              <img src={row.image_url} alt="" className="col-span-3 md:col-span-2 w-full aspect-[4/3] object-cover bg-muted" />
              <div className="col-span-5 md:col-span-3">
                <div className="serif text-sm">{new Date(row.date).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" })}</div>
                <div className="text-xs text-muted-foreground">{row.date}</div>
              </div>
              <div className="col-span-12 md:col-span-4">
                <div className="text-sm">{row.photographer_name ?? ""}</div>
                <div className="text-xs text-muted-foreground">{row.region ?? ""}</div>
              </div>
              <div className="col-span-4 md:col-span-3 text-right">
                <button onClick={() => onDelete(row.date)} disabled={!!busy}
                  className="eyebrow text-xs border border-border px-3 py-1 hover:border-destructive hover:text-destructive disabled:opacity-50">
                  {busy === `del-${row.date}` ? "…" : "Löschen"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}